import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useGraphQLClient, useSubscription } from "@package/graphql/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import type React from "react";
import { useSetAtom } from "jotai";

import {
  ActiveSessionSub,
  DeleteSession,
  ExcelLivenessProbeMutation,
  LivenessProbeMutation,
  StartSessionMut,
} from "./queries";
import { LogoutProvider, useLogout } from "../auth/provider";
import { isMobile } from "../../shared/hooks";
import { SessionView } from "./dialog";
import { gridLoadingAtom } from "../../stateAtoms";
import { isMultisessionCheckBypassed } from "./multisession";
import { useCurrentUser } from "../../data/user";
import { useSessionState, useUserId } from "../auth/hooks";

export const SessionProvider = ({ children }: { children: React.ReactNode }) => {
  useLivenessProbe(useUserId());
  const isMultiSessionUser = useIsMultiSessionUser();
  if (isMultiSessionUser.isLoading || isMultiSessionUser.multisession) {
    return <>{children}</>;
  }
  return <MultiSessionGuard>{children}</MultiSessionGuard>;
};

const MultiSessionGuard = ({ children }: { children: ReactNode }) => {
  const [loggingOut, setLoggingOut] = useState(false);
  const { mutate: startSessionMutate } = useHandlStartSession();
  const { mutateAsync: endSessionMutateAsync } = useHandleEndSession();
  const { data: sessionData } = useSessionState();
  const userId = useUserId();
  const logout = useLogout();
  const setGridLoading = useSetAtom(gridLoadingAtom);

  const { data: activeSubData, isLoading: activeSubIsLoading } = useSubscription(ActiveSessionSub, {
    variables: { user: userId, mobile: isMobile },
  });

  const active = activeSubData?.session?.[0];

  // If the session was created by Excel (id starts with "excel-"), it's not a webapp conflict
  const isExcelCreatedSession = active?.id?.startsWith("excel-");

  const notActiveUser =
    active?.id &&
    sessionData?.session_id &&
    active?.id !== sessionData?.session_id &&
    active?.is_mobile === isMobile &&
    !isExcelCreatedSession;

  // Logout the user if they are not the active session and their current
  // session is older than the active session
  // NOTE: last_seen is the time that the session token was created not actually the last time the user was active
  useEffect(() => {
    if (loggingOut) return;
    if (!notActiveUser) return;
    if (!sessionData?.session_start || !active?.session_start) return;
    if (sessionData.session_start > active.session_start) return;
    setLoggingOut(true);
    logout();
  }, [loggingOut, notActiveUser, logout, sessionData?.session_start, active?.session_start]);

  // Start the session if no active session exists, or if the existing one was created by Excel
  useEffect(() => {
    if (loggingOut || activeSubIsLoading || !sessionData?.session_id) {
      return;
    }
    // Start session if no active session OR if it was created by Excel (we take over)
    const shouldStartSession = !activeSubData?.session?.[0] || isExcelCreatedSession;
    if (!shouldStartSession) return;

    startSessionMutate({
      session_id: sessionData.session_id,
      session_start: sessionData.session_start,
    });
  }, [
    loggingOut,
    startSessionMutate,
    activeSubData,
    activeSubIsLoading,
    sessionData?.session_id,
    sessionData?.session_start,
    isExcelCreatedSession,
  ]);

  // Hide the loading screen when the session view is displayed
  useEffect(() => {
    if (notActiveUser) {
      setGridLoading(false);
    }
  }, [notActiveUser, setGridLoading]);

  const onLogout = useCallback(async () => {
    setLoggingOut(true);
    await endSessionMutateAsync().catch(console.error);
    return logout();
  }, [logout, endSessionMutateAsync]);

  if (!sessionData?.session_id) return null;

  if (notActiveUser) {
    return (
      <SessionView
        userId={userId}
        activeSession={active}
        currentSession={sessionData}
        onLogout={logout}
        onStartSession={() =>
          startSessionMutate({
            session_id: sessionData?.session_id,
            session_start: sessionData.session_start,
          })
        }
      />
    );
  }

  return <LogoutProvider value={onLogout}>{children}</LogoutProvider>;
};

const useHandlStartSession = () => {
  const graphql = useGraphQLClient();
  const userId = useUserId();
  return useMutation({
    mutationFn: async (p: { session_id: string; session_start: string }) => {
      await graphql.execute(StartSessionMut, {
        user: userId,
        mobile: isMobile,
        last_seen: new Date().toISOString(),
        ...p,
      });
    },
  });
};

const useHandleEndSession = () => {
  const graphql = useGraphQLClient();
  const userId = useUserId();
  return useMutation({
    mutationFn: () => graphql.execute(DeleteSession, { user: userId, mobile: isMobile }),
  });
};

const useIsMultiSessionUser = () => {
  const user = useCurrentUser();
  return {
    isLoading: user.isLoading,
    multisession: isMultisessionCheckBypassed(user.data),
  };
};

export function useLivenessProbe(userId?: string) {
  const graphql = useGraphQLClient();
  return useQuery({
    networkMode: "online",
    queryKey: ["livenessProbe", userId],
    refetchInterval: 30_000,
    refetchOnWindowFocus: "always",
    enabled: !!userId,
    queryFn: () => {
      if (!graphql || !userId) return null;
      return graphql.execute(LivenessProbeMutation, {
        user: userId,
        mobile: isMobile,
      });
    },
  });
}

export function useExcelLivenessProbe(userId?: string) {
  const graphql = useGraphQLClient();
  return useQuery({
    networkMode: "online",
    queryKey: ["excelLivenessProbe", userId],
    refetchInterval: 30_000,
    refetchOnWindowFocus: "always",
    enabled: !!userId,
    queryFn: () => {
      if (!graphql || !userId) return null;
      return graphql.execute(ExcelLivenessProbeMutation, {
        user: userId,
      });
    },
  });
}
